import React, { useEffect, useState } from 'react';
import { PlusIcon, Trash2Icon } from 'lucide-react';
import { useHrPayroll } from '../../../contexts/HrPayrollContext';
import { DeductionType, EarningType, LeavePolicy, LeaveType, PayrollSettings, SalaryGrade, StatutoryRuleSet, TaxBand } from '../../../types/hr';
import { defaultLeavePolicy, employmentTypes } from '../../../data/hrDefaults';
import { currentPeriod, formatDateTime, formatKwacha, makeId } from '../../../utils/hrFormat';
import { ruleSetForPeriod } from '../../../utils/payrollEngine';
import { Button } from '../../../components/ui/Button';
import { HrModal } from './HrModal';
type General = Omit<PayrollSettings, 'statutoryRuleSets' | 'earningTypes' | 'deductionTypes' | 'leaveTypes' | 'salaryGrades' | 'updatedAt' | 'updatedByName'>;
type RuleDraft = Omit<StatutoryRuleSet, 'id' | 'createdAt' | 'createdByName'>;
const cls = 'mt-1 block w-full rounded-md border border-slate-300 bg-white p-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500';
const num = (v: string) => v === '' ? 0 : Number(v);

/** Protected payroll configuration. Statutory rules are effective-dated; finalised payroll keeps its own snapshot. */
export function PayrollSettingsPage() {
  const {
    settings,
    saveSettings,
    addRuleSet,
    deleteRuleSet,
    payrollRuns
  } = useHrPayroll();
  const [general, setGeneral] = useState<General>(() => pickGeneral(settings));
  const [earnings, setEarnings] = useState<EarningType[]>(settings.earningTypes);
  const [deductions, setDeductions] = useState<DeductionType[]>(settings.deductionTypes);
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>(settings.leaveTypes);
  const [grades, setGrades] = useState<SalaryGrade[]>(settings.salaryGrades);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{
    ok: boolean;
    text: string;
  } | null>(null);
  const [ruleDraft, setRuleDraft] = useState<RuleDraft | null>(null);
  const [ruleError, setRuleError] = useState('');
  useEffect(() => {
    setGeneral(pickGeneral(settings));
    setEarnings(settings.earningTypes);
    setDeductions(settings.deductionTypes);
    setLeaveTypes(settings.leaveTypes);
    setGrades(settings.salaryGrades);
  }, [settings]);
  const leavePolicy: LeavePolicy = {
    ...defaultLeavePolicy,
    ...(general.leavePolicy ?? {})
  };
  const setLeavePolicy = (patch: Partial<LeavePolicy>) => setGeneral({
    ...general,
    leavePolicy: {
      ...leavePolicy,
      ...patch
    }
  });
  const inForce = ruleSetForPeriod(settings.statutoryRuleSets, currentPeriod());
  const usedRuleIds = new Set(payrollRuns.filter(r => r.status !== 'Draft').map(r => r.ruleSnapshot.id));
  const save = async () => {
    setMessage(null);
    if ([...earnings, ...deductions, ...leaveTypes, ...grades].some(item => !item.name.trim())) return setMessage({
      ok: false,
      text: 'Every category needs a name.'
    });
    if (general.paymentDay < 1 || general.paymentDay > 31) return setMessage({
      ok: false,
      text: 'Payment day must be between 1 and 31.'
    });
    if (!leaveTypes.some(t => t.id === leavePolicy.leaveTypeId)) return setMessage({
      ok: false,
      text: 'Choose which leave type accrues monthly.'
    });
    if (!(leavePolicy.daysPerMonth >= 0)) return setMessage({
      ok: false,
      text: 'Leave days per month cannot be negative.'
    });
    setSaving(true);
    const result = await saveSettings({
      ...general,
      earningTypes: earnings,
      deductionTypes: deductions,
      leaveTypes,
      salaryGrades: grades
    });
    setSaving(false);
    setMessage(result.ok ? {
      ok: true,
      text: 'Payroll settings saved. They apply to payroll calculated from now on.'
    } : {
      ok: false,
      text: result.error ?? 'Could not save settings.'
    });
  };
  const openNewRules = () => {
    setRuleError('');
    const base = inForce ?? settings.statutoryRuleSets[0];
    setRuleDraft({
      label: '',
      effectiveFrom: currentPeriod(),
      payeBands: base ? base.payeBands.map(b => ({
        ...b
      })) : [{
        upTo: null,
        rate: 0
      }],
      napsaEmployeeRate: base?.napsaEmployeeRate ?? 5,
      napsaEmployerRate: base?.napsaEmployerRate ?? 5,
      napsaMonthlyCeiling: base?.napsaMonthlyCeiling ?? 0,
      nhimaEmployeeRate: base?.nhimaEmployeeRate ?? 1,
      nhimaEmployerRate: base?.nhimaEmployerRate ?? 1,
      nhimaBasis: base?.nhimaBasis ?? 'basic',
      sdlRate: base?.sdlRate ?? 0.5,
      workersCompRate: base?.workersCompRate ?? 0
    });
  };
  const saveRules = async () => {
    if (!ruleDraft) return;
    setRuleError('');
    if (!ruleDraft.label.trim()) return setRuleError('Give the rule set a name, e.g. "2027 Budget".');
    if (settings.statutoryRuleSets.some(r => r.effectiveFrom === ruleDraft.effectiveFrom)) return setRuleError('A rule set already starts in that month.');
    const bands = ruleDraft.payeBands;
    if (bands.length === 0 || bands[bands.length - 1].upTo !== null) return setRuleError('The last PAYE band must have no upper limit.');
    for (let i = 1; i < bands.length; i += 1) {
      const prev = bands[i - 1].upTo;
      const cur = bands[i].upTo;
      if (prev === null || cur !== null && cur <= prev) return setRuleError('PAYE band limits must increase from top to bottom.');
    }
    const result = await addRuleSet(ruleDraft);
    if (!result.ok) return setRuleError(result.error ?? 'Could not save rule set.');
    setRuleDraft(null);
    setMessage({
      ok: true,
      text: `Statutory rules "${ruleDraft.label}" will apply from ${ruleDraft.effectiveFrom}.`
    });
  };
  const removeRules = async (rules: StatutoryRuleSet) => {
    if (!window.confirm(`Delete statutory rule set "${rules.label}"?`)) return;
    const result = await deleteRuleSet(rules.id);
    if (!result.ok) setMessage({
      ok: false,
      text: result.error ?? 'Could not delete.'
    });
  };
  const field = (key: keyof General, label: string, hint?: string, step = '1') => <div data-id="element-1560" data-mp="1j0jmv0n2cg5q">
      <label htmlFor={`ps-${key}`} className="block text-sm font-medium text-slate-700" data-id="element-1561" data-mp="1j09sz1ywdour">{label}</label>
      <input id={`ps-${key}`} type="number" step={step} min="0" value={String(general[key])} onChange={e => setGeneral({
      ...general,
      [key]: num(e.target.value)
    })} className={cls} data-id="element-1562" data-mp="26puhs1hqfgby" />
      {hint && <p className="mt-1 text-xs text-slate-500" data-id="element-1563" data-mp="2lcjnwm6horrb">{hint}</p>}
    </div>;
  return <div className="space-y-6" data-id="element-1564" data-mp="2gg2rz5t1t93o">
      {message && <p role={message.ok ? 'status' : 'alert'} className={`rounded-md border px-4 py-3 text-sm ${message.ok ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-red-200 bg-red-50 text-red-800'}`} data-id="element-1565" data-mp="2cdplgf6h975u">{message.text}</p>}

      <section className="rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-1566" data-mp="22gtjytezxlas">
        <header className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-5 py-3" data-id="element-1567" data-mp="1xojmmsbjxiyh">
          <div data-id="element-1568" data-mp="1sr08dseut66j">
            <h2 className="font-semibold text-slate-900" data-id="element-1569" data-mp="1sqi3y8i1flnj">Statutory rules (PAYE, NAPSA, NHIMA, SDL)</h2>
            <p className="text-xs text-slate-500" data-id="element-1570" data-mp="1opy95bkxsu4k">Effective-dated. Payroll uses the latest rule set starting on or before its month. Processed payroll keeps the rules it was calculated with.</p>
          </div>
          <Button size="sm" onClick={openNewRules} data-id="element-1571" data-mp="1eqz74buofwqj"><PlusIcon className="mr-1 h-4 w-4" aria-hidden="true" data-id="element-1572" data-mp="3qdsxtuijaaa0" /> New rule set</Button>
        </header>
        <ul className="divide-y divide-slate-100" data-id="element-1573" data-mp="3vc49mase2uuv">
          {[...settings.statutoryRuleSets].sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom)).map(rules => <li key={rules.id} className="grid gap-4 px-5 py-4 lg:grid-cols-[220px_minmax(0,1fr)_minmax(0,1fr)_auto]" data-id="element-1574" data-mp="87xussbhzog5">
              <div data-id="element-1575" data-mp="37nl2hyl2so4d">
                <p className="font-semibold text-slate-900" data-id="element-1576" data-mp="37o49wld3nu45">{rules.label}</p>
                <p className="text-xs text-slate-500" data-id="element-1577" data-mp="3clmk0f2usfdq">From {rules.effectiveFrom}</p>
                {inForce?.id === rules.id && <span className="mt-1 inline-block rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800" data-id="element-1578" data-mp="3gnhi7aphmcna">In force</span>}
              </div>
              <div data-id="element-1579" data-mp="2owjtsiu5le7m">
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-1580" data-mp="2ox3do53d6coa">PAYE monthly bands</p>
                <ul className="space-y-0.5 text-sm tabular-nums" data-id="element-1581" data-mp="2sx3qxmeq8htr">
                  {rules.payeBands.map((band, i) => <li key={i} data-id="element-1582" data-mp="1mbcohsr5fyad" data-mpi={i}>{i === 0 ? 'First' : 'Next'} {band.upTo === null ? 'balance above ' + formatKwacha(rules.payeBands[i - 1]?.upTo ?? 0) : `up to ${formatKwacha(band.upTo)}`} — <strong data-id="element-1583" data-mp="1w9abkcx9vp1e">{band.rate}%</strong></li>)}
                </ul>
              </div>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-sm" data-id="element-1584" data-mp="1r94j4uj6hyi9">
                <dt className="text-slate-500" data-id="element-1585" data-mp="2596zudfr2lvx">NAPSA</dt><dd className="tabular-nums" data-id="element-1586" data-mp="25843hpznrr8m">{rules.napsaEmployeeRate}% + {rules.napsaEmployerRate}%</dd>
                <dt className="text-slate-500" data-id="element-1587" data-mp="20ac4yd4id6vg">NAPSA ceiling</dt><dd className="tabular-nums" data-id="element-1588" data-mp="2f01i76pvl1c3">{formatKwacha(rules.napsaMonthlyCeiling)}</dd>
                <dt className="text-slate-500" data-id="element-1589" data-mp="2a0p0f0dv2kgd">NHIMA</dt><dd className="tabular-nums" data-id="element-1590" data-mp="2a1q8qx3hssue">{rules.nhimaEmployeeRate}% + {rules.nhimaEmployerRate}% of {rules.nhimaBasis}</dd>
                <dt className="text-slate-500" data-id="element-1591" data-mp="2ny3sydzev8ck">SDL</dt><dd className="tabular-nums" data-id="element-1592" data-mp="2jz4osvli7fm6">{rules.sdlRate}%</dd>
                <dt className="text-slate-500" data-id="element-1593" data-mp="2jwry9u1ojwxi">Workers’ Comp</dt><dd className="tabular-nums" data-id="element-1594" data-mp="32nyf7oetkbbi">{rules.workersCompRate}%</dd>
              </dl>
              <div data-id="element-1595" data-mp="37m8as04ghdxn">
                {!usedRuleIds.has(rules.id) && settings.statutoryRuleSets.length > 1 && <button type="button" onClick={() => removeRules(rules)} className="rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600" aria-label={`Delete ${rules.label}`} data-id="element-1596" data-mp="2tl5p3ohdi5n9"><Trash2Icon className="h-4 w-4" data-id="element-1597" data-mp="2jwnpvboqs6lq" /></button>}
              </div>
            </li>)}
        </ul>
        <p className="border-t border-slate-100 px-5 py-3 text-xs text-slate-500" data-id="element-1598" data-mp="2onvzo8tzhtzf">Verify rates against the current ZRA, NAPSA and NHIMA notices before each budget year. Rule sets already used by approved payroll cannot be deleted.</p>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-id="element-1599" data-mp="20xjhm550ygv4">
        <h2 className="font-semibold text-slate-900" data-id="element-1600" data-mp="20zw0qlmiqx9r">Payment, overtime &amp; duty rules</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-1601" data-mp="25ypbi6af8a68">
          {field('paymentDay', 'Payment day of month')}
          {field('workingDaysPerMonth', 'Working days per month', 'Used for daily rate and absences')}
          {field('hoursPerDay', 'Hours per day', 'Used for hourly overtime rate')}
          {field('overtimeMultiplier', 'Overtime multiplier', '× hourly rate', '0.05')}
          {field('publicHolidayMultiplier', 'Public holiday multiplier', '× daily rate', '0.05')}
          {field('nightDutyRate', 'Night duty allowance (K / shift)', undefined, '0.01')}
          {field('weekendDutyRate', 'Weekend duty allowance (K / shift)', undefined, '0.01')}
          {field('onCallRate', 'On-call allowance (K / duty)', undefined, '0.01')}
          <div data-id="element-1602" data-mp="l1fvh826jx6g">
            <label htmlFor="ps-rounding" className="block text-sm font-medium text-slate-700" data-id="element-1603" data-mp="l0wg9z3wzmw4">Net pay rounding</label>
            <select id="ps-rounding" value={general.netPayRounding} onChange={e => setGeneral({
            ...general,
            netPayRounding: e.target.value as General['netPayRounding']
          })} className={cls} data-id="element-1604" data-mp="18qid4fwtdq0y">
              <option value="none" data-id="element-1605" data-mp="2y7chu7szqpuh">No rounding (to the ngwee)</option>
              <option value="nearest-kwacha" data-id="element-1606" data-mp="2t9ks9kftjftg">Nearest kwacha</option>
            </select>
          </div>
          <div data-id="element-1607" data-mp="37192fi6ibu7s">
            <label htmlFor="ps-payslip" className="block text-sm font-medium text-slate-700" data-id="element-1608" data-mp="370zes18xpz59">Payslip number prefix</label>
            <input id="ps-payslip" value={general.payslipPrefix} onChange={e => setGeneral({
            ...general,
            payslipPrefix: e.target.value.toUpperCase()
          })} className={cls} data-id="element-1609" data-mp="2fal40dw7x2xt" />
            <p className="mt-1 text-xs text-slate-500" data-id="element-1610" data-mp="2adcic3bv2grt">e.g. {general.payslipPrefix}-{new Date().getFullYear()}-0001</p>
          </div>
          <div data-id="element-1611" data-mp="2jcg2lamrl27w">
            <label htmlFor="ps-emp" className="block text-sm font-medium text-slate-700" data-id="element-1612" data-mp="2jbofypb5aetr">Employee number prefix</label>
            <input id="ps-emp" value={general.employeePrefix} onChange={e => setGeneral({
            ...general,
            employeePrefix: e.target.value.toUpperCase()
          })} className={cls} data-id="element-1613" data-mp="3k0jsw3ygqc0" />
            <p className="mt-1 text-xs text-slate-500" data-id="element-1614" data-mp="2segmjq341lq1">e.g. {general.employeePrefix}-0001</p>
          </div>
          <label className="flex items-center gap-2 self-center text-sm text-slate-700" data-id="element-1615" data-mp="2xc8pdf5ams90">
            <input type="checkbox" checked={general.deductAbsences} onChange={e => setGeneral({
            ...general,
            deductAbsences: e.target.checked
          })} className="rounded border-slate-300" data-id="element-1616" data-mp="31e3pigbhmzl7" />
            Deduct approved absences from basic pay
          </label>
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" aria-labelledby="ps-leave-title" data-id="element-1617" data-mp="2dp102mam42g5">
        <h2 id="ps-leave-title" className="font-semibold text-slate-900" data-id="element-1618" data-mp="2inlyqha7ue2h">Leave accrual</h2>
        <p className="text-xs text-slate-500" data-id="element-1619" data-mp="2nekkvxhvy6dn">Eligible employees earn leave automatically for every completed month of service. The balance appears on every payslip as the Employee Leave Summary.</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-1620" data-mp="1uy9pxiisw80p">
          <div data-id="element-1621" data-mp="1uw6s4cbepalf">
            <label htmlFor="ps-lv-type" className="block text-sm font-medium text-slate-700" data-id="element-1622" data-mp="1nes5m2xqfe33">Accruing leave type</label>
            <select id="ps-lv-type" value={leavePolicy.leaveTypeId} onChange={e => setLeavePolicy({
            leaveTypeId: e.target.value
          })} className={cls} data-id="element-1623" data-mp="1ifnwfcat5gcr">
              {leaveTypes.map(t => <option key={t.id} value={t.id} data-id="element-1624" data-mp="19f96u0qt9f3g">{t.name || 'Unnamed'}</option>)}
            </select>
          </div>
          <div data-id="element-1625" data-mp="14go9c92y19ip">
            <label htmlFor="ps-lv-rate" className="block text-sm font-medium text-slate-700" data-id="element-1626" data-mp="14h7rnqwi9zx9">Days earned per completed month</label>
            <input id="ps-lv-rate" type="number" min="0" step="0.5" value={leavePolicy.daysPerMonth} onChange={e => setLeavePolicy({
            daysPerMonth: num(e.target.value)
          })} className={cls} data-id="element-1627" data-mp="zo4u77oly6bs" />
            <p className="mt-1 text-xs text-slate-500" data-id="element-1628" data-mp="psuuwrplgd1p">{leavePolicy.daysPerMonth * 12} days per year</p>
          </div>
          <div data-id="element-1629" data-mp="lrrbjv8c7yvq">
            <label htmlFor="ps-lv-cap" className="block text-sm font-medium text-slate-700" data-id="element-1630" data-mp="gqlitxeogbm7">Carry-forward limit (days)</label>
            <input id="ps-lv-cap" type="number" min="0" step="0.5" value={leavePolicy.maxCarryForwardDays} onChange={e => setLeavePolicy({
            maxCarryForwardDays: num(e.target.value)
          })} className={cls} data-id="element-1631" data-mp="2gsl2fwcno4yx" />
            <p className="mt-1 text-xs text-slate-500" data-id="element-1632" data-mp="273tdlx2w60s8">0 = all unused leave carries forward. Otherwise days above the limit lapse on 1 January.</p>
          </div>
        </div>
        <fieldset className="mt-4" data-id="element-1633" data-mp="1x53vc3732ebg">
          <legend className="text-sm font-medium text-slate-700" data-id="element-1634" data-mp="1x3svufko7sq5">Employment types that accrue leave</legend>
          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-2" data-id="element-1635" data-mp="224qc2mm4ecp0">
            {employmentTypes.map(type => <label key={type} className="inline-flex items-center gap-2 text-sm text-slate-700" data-id="element-1636" data-mp="1o60bgx03duvw">
                <input type="checkbox" className="rounded border-slate-300" checked={leavePolicy.eligibleEmploymentTypes.includes(type)} onChange={e => setLeavePolicy({
              eligibleEmploymentTypes: e.target.checked ? [...leavePolicy.eligibleEmploymentTypes, type] : leavePolicy.eligibleEmploymentTypes.filter(t => t !== type)
            })} data-id="element-1637" data-mp="1t3ioayh8cu96" />
                {type}
              </label>)}
          </div>
        </fieldset>
        <p className="mt-3 text-xs text-slate-500" data-id="element-1638" data-mp="a7w1dihtx7x8">Changes recalculate current balances. Payslips already issued keep the leave figures they were generated with.</p>
      </section>

      <div className="grid gap-6 xl:grid-cols-2" data-id="element-1639" data-mp="5fkzia3j3c21">
        <ListEditor title="Allowance & earning categories" items={earnings} onChange={setEarnings} make={() => ({
        id: makeId('earn'),
        name: '',
        taxable: true,
        pensionable: true
      })} flags={[{
        key: 'taxable',
        label: 'Taxable'
      }, {
        key: 'pensionable',
        label: 'NAPSA'
      }]} data-id="element-1640" data-mp="k58z96w2a2nn" />
        <ListEditor title="Deduction categories" items={deductions} onChange={setDeductions} make={() => ({
        id: makeId('ded'),
        name: '',
        preTax: false
      })} flags={[{
        key: 'preTax',
        label: 'Before PAYE'
      }]} data-id="element-1641" data-mp="p4vcvhfsxkwz" />
        <ListEditor title="Leave types" items={leaveTypes} onChange={setLeaveTypes} make={() => ({
        id: makeId('leave'),
        name: '',
        entitlementDays: 0,
        paid: true,
        accruesMonthly: false
      })} flags={[{
        key: 'paid',
        label: 'Paid'
      }, {
        key: 'accruesMonthly',
        label: 'Accrues monthly'
      }]} numbers={[{
        key: 'entitlementDays',
        label: 'Days / year'
      }]} data-id="element-1642" data-mp="2qxtdhzftlhsh" />
        <ListEditor title="Salary grades (structures)" items={grades} onChange={setGrades} make={() => ({
        id: makeId('grade'),
        name: '',
        minimum: 0,
        maximum: 0
      })} numbers={[{
        key: 'minimum',
        label: 'Min (K)'
      }, {
        key: 'maximum',
        label: 'Max (K)'
      }]} data-id="element-1643" data-mp="18tejk0jfz37c" />
      </div>

      <div className="sticky bottom-0 flex flex-wrap items-center justify-end gap-3 border-t border-slate-200 bg-slate-50/95 py-3 backdrop-blur" data-id="element-1644" data-mp="1hs9o786u858j">
        {settings.updatedAt && <p className="mr-auto text-xs text-slate-500" data-id="element-1645" data-mp="1rikuv2hwosj4">Last saved {formatDateTime(settings.updatedAt)} by {settings.updatedByName}</p>}
        <Button onClick={save} isLoading={saving} data-id="element-1646" data-mp="1wgw6nirrhd3z">Save payroll settings</Button>
      </div>

      {ruleDraft && <HrModal title="New statutory rule set" subtitle="Applies prospectively from the chosen month" size="lg" onClose={() => setRuleDraft(null)} footer={<>{ruleError && <p role="alert" className="mr-auto self-center text-sm text-red-700" data-id="element-1648" data-mp="3gzq6ypv31nin">{ruleError}</p>}<Button variant="outline" onClick={() => setRuleDraft(null)} data-id="element-1649" data-mp="3c2gk9hz2vpyi">Cancel</Button><Button onClick={saveRules} data-id="element-1650" data-mp="373n7jrrm82ze">Save rule set</Button></>} data-id="element-1647" data-mp="3l2mtgrnqu3rv">
          <div className="space-y-5" data-id="element-1651" data-mp="8l83z2k00y5w">
            <div className="grid gap-4 sm:grid-cols-2" data-id="element-1652" data-mp="8k6fnwdz2f68">
              <div data-id="element-1653" data-mp="3m4yxi40qrxx"><label htmlFor="rs-label" className="block text-sm font-medium text-slate-700" data-id="element-1654" data-mp="3m4w7acmwdu8">Name</label><input id="rs-label" value={ruleDraft.label} onChange={e => setRuleDraft({
              ...ruleDraft,
              label: e.target.value
            })} placeholder="e.g. 2027 Budget" className={cls} data-id="element-1655" data-mp="3ohfpry1iw9e" /></div>
              <div data-id="element-1656" data-mp="3pzll93jxpymw"><label htmlFor="rs-from" className="block text-sm font-medium text-slate-700" data-id="element-1657" data-mp="3pzlk30877slb">Effective from</label><input id="rs-from" type="month" value={ruleDraft.effectiveFrom} onChange={e => setRuleDraft({
              ...ruleDraft,
              effectiveFrom: e.target.value
            })} className={cls} data-id="element-1658" data-mp="rdsfb63nmkly" /></div>
            </div>
            <BandEditor bands={ruleDraft.payeBands} onChange={payeBands => setRuleDraft({
          ...ruleDraft,
          payeBands
        })} data-id="element-1659" data-mp="3r0za8yjyim5b" />
            <div className="grid gap-4 sm:grid-cols-3" data-id="element-1660" data-mp="3i2dufb5vdl88">
              {([['napsaEmployeeRate', 'NAPSA employee %'], ['napsaEmployerRate', 'NAPSA employer %'], ['napsaMonthlyCeiling', 'NAPSA monthly ceiling (K)'], ['nhimaEmployeeRate', 'NHIMA employee %'], ['nhimaEmployerRate', 'NHIMA employer %'], ['sdlRate', 'Skills Development Levy %'], ['workersCompRate', 'Workers’ Compensation %']] as [keyof RuleDraft, string][]).map(([key, label]) => <div key={key} data-id="element-1661" data-mp="9m29vdimwr8p"><label htmlFor={`rs-${key}`} className="block text-sm font-medium text-slate-700" data-id="element-1662" data-mp="9lss2d7qtqe3">{label}</label>
                  <input id={`rs-${key}`} type="number" step="0.01" min="0" value={String(ruleDraft[key])} onChange={e => setRuleDraft({
              ...ruleDraft,
              [key]: num(e.target.value)
            })} className={cls} data-id="element-1663" data-mp="2pcf0p2gqczx8" /></div>)}
              <div data-id="element-1664" data-mp="2lty2w94kji7m"><label htmlFor="rs-basis" className="block text-sm font-medium text-slate-700" data-id="element-1665" data-mp="2ltpo2bovy2ts">NHIMA basis</label>
                <select id="rs-basis" value={ruleDraft.nhimaBasis} onChange={e => setRuleDraft({
              ...ruleDraft,
              nhimaBasis: e.target.value as RuleDraft['nhimaBasis']
            })} className={cls} data-id="element-1666" data-mp="2hw0ayyjeyi0j"><option value="basic" data-id="element-1667" data-mp="2qvx5vhwecd41">Basic salary</option><option value="gross" data-id="element-1668" data-mp="2qssr5qlb1yz4">Gross pay</option></select></div>
            </div>
          </div>
        </HrModal>}
    </div>;
}
function pickGeneral(s: PayrollSettings): General {
  const {
    statutoryRuleSets,
    earningTypes,
    deductionTypes,
    leaveTypes,
    salaryGrades,
    updatedAt,
    updatedByName,
    ...rest
  } = s;
  return {
    ...rest,
    leavePolicy: {
      ...defaultLeavePolicy,
      ...(rest.leavePolicy ?? {})
    }
  };
}
function BandEditor({
  bands,
  onChange
}: {
  bands: TaxBand[];
  onChange: (bands: TaxBand[]) => void;
}) {
  const update = (i: number, patch: Partial<TaxBand>) => onChange(bands.map((b, j) => j === i ? {
    ...b,
    ...patch
  } : b));
  return <fieldset data-id="element-1669" data-mp="bwbz9reqc13z">
      <legend className="text-sm font-medium text-slate-700" data-id="element-1670" data-mp="bvshcap2xcpy">PAYE monthly bands</legend>
      <div className="mt-2 space-y-2" data-id="element-1671" data-mp="6xilryzg0a3t">
        {bands.map((band, i) => {
        const last = i === bands.length - 1;
        return <div key={i} className="flex flex-wrap items-center gap-2 text-sm" data-id="element-1672" data-mp="lmwur5z3wpio" data-mpi={i}>
              <span className="w-12 text-slate-500" data-id="element-1673" data-mp="gsakj64rh306">{i === 0 ? 'First' : 'Next'}</span>
              {last ? <span className="w-40 text-slate-600" data-id="element-1674" data-mp="ujxjxeu02rn6">balance (no limit)</span> : <label className="flex items-center gap-1" data-id="element-1675" data-mp="pm5quhhmczhe"><span className="sr-only" data-id="element-1676" data-mp="14jy3n1vs79rw">Band {i + 1} upper limit</span>up to K<input type="number" min="0" step="0.01" value={band.upTo ?? ''} onChange={e => update(i, {
              upTo: num(e.target.value)
            })} className="w-28 rounded-md border border-slate-300 p-1.5" data-id="element-1677" data-mp="14jg1jomfu1c2" /></label>}
              <label className="flex items-center gap-1" data-id="element-1678" data-mp="36d5v19oo5hu5"><span className="sr-only" data-id="element-1679" data-mp="31czzvjj6xd7b">Band {i + 1} rate</span><input type="number" min="0" max="100" step="0.5" value={band.rate} onChange={e => update(i, {
              rate: num(e.target.value)
            })} className="w-20 rounded-md border border-slate-300 p-1.5" data-id="element-1680" data-mp="31eb39i8q4ixw" />%</label>
              {bands.length > 1 && !last && <button type="button" onClick={() => onChange(bands.filter((_, j) => j !== i))} className="rounded p-1 text-slate-400 hover:text-red-600" aria-label={`Remove band ${i + 1}`} data-id="element-1681" data-mp="174w6oud7bg3h"><Trash2Icon className="h-4 w-4" data-id="element-1682" data-mp="1c14hxwoa2p5d" /></button>}
            </div>;
      })}
      </div>
      <button type="button" onClick={() => onChange([...bands.slice(0, -1), {
      upTo: (bands[bands.length - 2]?.upTo ?? 0) + 1000,
      rate: 0
    }, bands[bands.length - 1]])} className="mt-2 text-sm font-medium text-blue-700 hover:underline" data-id="element-1683" data-mp="125b66fiduzon">+ Add band</button>
    </fieldset>;
}
type Editable = {
  id: string;
  name: string;
  system?: boolean;
};
function ListEditor<T extends Editable>({
  title,
  items,
  onChange,
  make,
  flags = [],
  numbers = []
}: {
  title: string;
  items: T[];
  onChange: (items: T[]) => void;
  make: () => T;
  flags?: {
    key: keyof T;
    label: string;
  }[];
  numbers?: {
    key: keyof T;
    label: string;
  }[];
}) {
  const update = (id: string, patch: Partial<T>) => onChange(items.map(item => item.id === id ? {
    ...item,
    ...patch
  } : item));
  return <section className="rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-1684" data-mp="2c3t0lv31sybr">
      <header className="flex items-center justify-between border-b border-slate-100 px-5 py-3" data-id="element-1685" data-mp="2759cbo0zfag8">
        <h2 className="font-semibold text-slate-900" data-id="element-1686" data-mp="34qwxfcrzm6cg">{title}</h2>
        <Button size="sm" variant="outline" onClick={() => onChange([...items, make()])} data-id="element-1687" data-mp="2zsvjt7dbyz8d"><PlusIcon className="mr-1 h-4 w-4" aria-hidden="true" data-id="element-1688" data-mp="2zv7vj2shwdcz" /> Add</Button>
      </header>
      <ul className="divide-y divide-slate-100" data-id="element-1689" data-mp="2v02ccbqh9csl">
        {items.map(item => <li key={item.id} className="flex flex-wrap items-center gap-3 px-5 py-2.5 text-sm" data-id="element-1690" data-mp="2q2iw56a7ypy0">
            <label className="min-w-[160px] flex-1" data-id="element-1691" data-mp="3nk0vrbg5h7de"><span className="sr-only" data-id="element-1692" data-mp="3irpvg7hhzjkb">Name</span>
              <input value={item.name} disabled={item.system} onChange={e => update(item.id, {
            name: e.target.value
          } as Partial<T>)} placeholder="Name" className="w-full rounded-md border border-slate-300 p-1.5 disabled:border-transparent disabled:bg-transparent disabled:font-medium disabled:text-slate-900" data-id="element-1693" data-mp="3iqy08xrtbnuk" />
            </label>
            {numbers.map(n => <label key={String(n.key)} className="flex items-center gap-1 text-xs text-slate-600" data-id="element-1694" data-mp="8va995yzxrn0">{n.label}
                <input type="number" min="0" step="0.01" value={String(item[n.key] ?? 0)} onChange={e => update(item.id, {
            [n.key]: num(e.target.value)
          } as Partial<T>)} className="w-24 rounded-md border border-slate-300 p-1.5 text-sm" data-id="element-1695" data-mp="dun52g7ui91q" />
              </label>)}
            {flags.map(f => <label key={String(f.key)} className="inline-flex items-center gap-1.5 text-xs text-slate-600" data-id="element-1696" data-mp="rk73j9ue8k58">
                <input type="checkbox" checked={Boolean(item[f.key])} onChange={e => update(item.id, {
            [f.key]: e.target.checked
          } as Partial<T>)} className="rounded border-slate-300" data-id="element-1697" data-mp="wlmrparvb6ru" />{f.label}
              </label>)}
            {item.system ? <span className="text-xs text-slate-400" data-id="element-1698" data-mp="1tbq6e90rs3cp">System</span> : <button type="button" onClick={() => onChange(items.filter(i => i.id !== item.id))} className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600" aria-label={`Remove ${item.name || 'item'}`} data-id="element-1699" data-mp="1t9m57yu99ig4"><Trash2Icon className="h-4 w-4" data-id="element-1700" data-mp="233mgyjk1llxt" /></button>}
          </li>)}
      </ul>
    </section>;
}